import useApi from './useApi'

const instances = new Map<string | number, ReturnType<typeof useExtendedApi>>()

export default function useApiFactory(
  endpoint: string,
  includes: string[] = [],
  key?: string | number
) {
  const endpointKey = `${endpoint}_${includes}_${key}`

  const instance = instances.get(endpointKey) || useExtendedApi(endpoint, includes)
  instances.set(endpointKey, instance)

  return instance
}

export const rowsPerPageOptions = [10, 20, 50, 100]

function useExtendedApi(endpoint: string, includes: string[] = []) {
  const data = ref([])

  const meta = ref({
    current_page: 0,
    from: 0,
    to: 0,
    total: 0,
    last_page: 0,
    per_page: rowsPerPageOptions[1]
  })
  const links = ref({})
  const error = ref(null)
  const loading = ref(false)

  const currentSortField = ref('id')
  const currentSortOrder = ref<0 | 1 | -1 | null | undefined>(-1)

  const currentFilters = ref<{ [prop: string]: string[] }>({})
  const localFilter = ref<(item: any) => boolean>()

  const fetch = async (
    pageNumber = 1,
    sortField = 'id',
    sortOrder?: 0 | 1 | -1 | null,
    filters?: { [prop: string]: string[] },
    shouldAppendNextPage?: boolean
  ) => {
    const sortOrderString = sortOrder === 1 ? '' : '-'

    currentSortField.value = sortField
    currentSortOrder.value = sortOrder
    currentFilters.value = filters || {}

    const filtersQuery = Object.keys(currentFilters.value)
      .map((key) =>
        currentFilters.value[key]?.length
          ? `filter[${key}]=${currentFilters.value[key].join()}`
          : ''
      )
      .filter((value) => value)
      .join('&')

    const sort = `${sortOrderString}${sortField}`
    const include = includes.length ? '&include=' + includes.join(',') : ''

    const url = `${endpoint}?page=${pageNumber}&per_page=${
      meta.value.per_page
    }&sort=${sort}${include}${filtersQuery ? '&' : ''}${filtersQuery}`

    loading.value = true
    const response = await useApi(url, 'GET')

    const responseData = localFilter.value ? response.data.filter(localFilter.value) : response.data

    data.value = shouldAppendNextPage ? [...data.value, ...responseData] : responseData
    meta.value = response.meta
    links.value = response.links

    loading.value = false

    return { response }
  }

  const resetSelection = ref<(() => void) | null>(null)

  function setSelectionResetter(selectedItems: Ref<any[]>) {
    resetSelection.value = () => {
      selectedItems.value = []
    }
  }

  const refresh = async () => {
    resetSelection.value?.()

    await fetch(
      meta.value.current_page,
      currentSortField.value,
      currentSortOrder.value,
      currentFilters.value
    )
  }

  async function fetchNextPage() {
    if (meta.value.current_page !== meta.value.last_page) {
      return await fetch(
        meta.value.current_page + 1,
        currentSortField.value,
        currentSortOrder.value,
        currentFilters.value,
        true
      )
    }
  }

  async function applyFilter(field: string, value: any[]) {
    currentFilters.value[field] = value
    meta.value.current_page = 1
    await refresh()
  }

  async function applyFilters(filters: { [field: string]: any[] }) {
    currentFilters.value = { ...currentFilters.value, ...filters }
    meta.value.current_page = 1
    await refresh()
  }

  async function setFilters(filters: { [field: string]: any[] }, preventRefresh?: boolean) {
    currentFilters.value = { ...filters }
    meta.value.current_page = 1
    if (preventRefresh) return
    await refresh()
  }

  function changeSort(sortField: string, sortOrder?: 0 | 1 | -1 | null) {
    currentSortField.value = sortField || 'id'
    currentSortOrder.value = sortOrder
    meta.value.current_page = 1
  }

  async function applySort(sortField: string, sortOrder?: 0 | 1 | -1 | null) {
    changeSort(sortField, sortOrder)
    await refresh()
  }

  async function changePage(pageNumber: number) {
    meta.value.current_page = pageNumber
    await refresh()
  }

  function changePerPage(perPage: number) {
    meta.value.per_page = perPage
    meta.value.current_page = 1
  }

  async function applyPerPage(perPage: number) {
    changePerPage(perPage)
    await refresh()
  }

  function updateLocalObject(id: number | string, field: string, value: any) {
    const object = (data.value as { id: number }[]).find((o) => o.id === id)

    if (object) {
      ;(object as any)[field] = value
    }
  }

  function updateLocalObjectPart(id: number | string, update: { [field: string]: any }) {
    const object = (data.value as { id: number }[]).find((o) => o.id === id)

    if (object) {
      Object.assign(object, update)
    }
  }

  function deleteLocalObject(id: number) {
    const index = (data.value as { id: number }[]).findIndex((o) => o.id === id)
    data.value.splice(index, 1)
  }

  function resetData() {
    data.value = []
  }

  return {
    fetch,
    endpoint,
    meta,
    links,
    error,
    data,
    loading,
    currentFilters,
    localFilter,
    resetData,
    refresh,
    applyFilter,
    applyFilters,
    setFilters,
    fetchNextPage,
    changeSort,
    applySort,
    changePage,
    changePerPage,
    applyPerPage,
    updateLocalObject,
    updateLocalObjectPart,
    deleteLocalObject,
    setSelectionResetter
  }
}
